/**
 * Payments admin contracts (U9) — Stripe Connect onboarding + the school-level
 * fee-bearer default, consumed by the web events dashboard.
 *
 * Only the ADMIN configuration shapes live here. The buyer-checkout money
 * shapes (`client_secret`, PaymentIntent status, U8) stay backend/web-local —
 * they are not shared clients' concern (mirrors the note in this module's
 * index: event/order request bodies remain backend-local; only net-new
 * web-consumed contracts are shared).
 *
 * Backing endpoints (all school-admin authed):
 *   POST  /v1/ticketed-events/payments/onboarding/start   -> StartPaymentOnboardingResult
 *   GET   /v1/ticketed-events/payments/onboarding/status  -> PaymentOnboardingStatus
 *   PATCH /v1/ticketed-events/payments/settings           -> PaymentAccountAdminView
 *
 * Each success body is wrapped in `{ data: ... }` by the backend result
 * handler; the schemas below describe the inner `data` payload.
 */

import { z } from 'zod'

/**
 * Who nets the two-layer fee on a charge (R4). `buyer_pays` adds the fee on
 * top (buyer sees a service fee, school nets face value); `school_absorbs`
 * charges only the subtotal and the school eats the fee. Mirrors the Prisma
 * `PaymentFeeBearer` enum + the fee-engine's `FeeBearer` string-union.
 */
export const PaymentFeeBearerSchema = z.enum(['buyer_pays', 'school_absorbs'])
export type PaymentFeeBearer = z.infer<typeof PaymentFeeBearerSchema>

/**
 * The payee view surfaced to the admin dashboard — a deliberately narrow
 * subset of `SchoolPaymentAccount` (the Stripe connected-account id and the
 * `lossesPayer` posture are intentionally omitted from this admin surface).
 * `onboardingStatus` mirrors the entity's `PaymentAccountOnboardingStatus`
 * enum (kept inline to avoid a duplicate top-level export).
 */
export const PaymentAccountAdminViewSchema = z.object({
  publicId: z.string(),
  onboardingStatus: z.enum(['pending', 'onboarding', 'active', 'restricted']),
  chargesEnabledAt: z.string().nullable(),
  feeBearerDefault: PaymentFeeBearerSchema,
  isPrimary: z.boolean()
})
export type PaymentAccountAdminView = z.infer<typeof PaymentAccountAdminViewSchema>

/** GET /payments/onboarding/status — inner `data`. */
export const PaymentOnboardingStatusSchema = z.object({
  /** False when the school has never started onboarding (no payee row yet). */
  connected: z.boolean(),
  chargesEnabled: z.boolean(),
  /** Stripe `requirements.currently_due` — outstanding onboarding items. */
  currentlyDue: z.array(z.string()),
  account: PaymentAccountAdminViewSchema.nullable()
})
export type PaymentOnboardingStatus = z.infer<typeof PaymentOnboardingStatusSchema>

/** GET /payments/onboarding/status — the whole body. */
export const PaymentOnboardingStatusResponseSchema = z.object({ data: PaymentOnboardingStatusSchema })
export type PaymentOnboardingStatusResponse = z.infer<typeof PaymentOnboardingStatusResponseSchema>

/** POST /payments/onboarding/start — request body. */
export const StartPaymentOnboardingRequestSchema = z.object({
  /**
   * Confirmed `losses.payments` posture (KTD2) — required the first time a
   * school onboards (immutable once the account is minted), ignored on a
   * repeat call. Optional here so the not-yet-confirmed case fails through the
   * backend's typed KTD2 gate rather than a bare schema rejection.
   */
  lossesPayer: z.enum(['stripe', 'application']).optional(),
  returnUrl: z.string().url(),
  refreshUrl: z.string().url()
})
export type StartPaymentOnboardingRequest = z.infer<typeof StartPaymentOnboardingRequestSchema>

/** POST /payments/onboarding/start — inner `data`. */
export const StartPaymentOnboardingResultSchema = z.object({
  /** Short-lived Stripe-hosted Account Link the admin is redirected to. */
  url: z.string(),
  account: PaymentAccountAdminViewSchema
})
export type StartPaymentOnboardingResult = z.infer<typeof StartPaymentOnboardingResultSchema>

/** POST /payments/onboarding/start — the whole body. */
export const StartPaymentOnboardingResponseSchema = z.object({ data: StartPaymentOnboardingResultSchema })
export type StartPaymentOnboardingResponse = z.infer<typeof StartPaymentOnboardingResponseSchema>

/** PATCH /payments/settings — request body (set the school-level default). */
export const UpdatePaymentSettingsRequestSchema = z.object({
  feeBearerDefault: PaymentFeeBearerSchema
})
export type UpdatePaymentSettingsRequest = z.infer<typeof UpdatePaymentSettingsRequestSchema>

/** PATCH /payments/settings — the whole body. */
export const UpdatePaymentSettingsResponseSchema = z.object({ data: PaymentAccountAdminViewSchema })
export type UpdatePaymentSettingsResponse = z.infer<typeof UpdatePaymentSettingsResponseSchema>
